import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, Modal, Form, ModalBody } from "reactstrap";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { addNewClient as onAddNewClient, updateClient as onUpdateClient } from "../../../slices/thunks";
import "react-toastify/dist/ReactToastify.css";
import FieldsStep from "./FieldsStep";
import LinkedStep from "./LinkedStep";
import ReviewStep from "./ReviewStep";
import {
  getIdentityFields,
  getContactFields,
  getAddressFields,
  getBankingFields,
} from "./fieldConfig";
import { getStepValidationSchema, countStepIssues } from "./ValidationSchemas";

// Cleared when switching kind mid-edit — the previous kind's identity data
// shouldn't linger on the record once it no longer applies. Contact/Address/
// Banking are shared between kinds already, so they're left untouched.
const NATURAL_IDENTITY_KEYS = [
  "name",
  "surname",
  "profession",
  "social_security_number",
  "social_security_series",
  "passport",
];
const LEGAL_IDENTITY_KEYS = [
  "legal_company_name",
  "legal_fiscal_code",
  "legal_unique_registration_code",
  "legal_trade_register_number",
];

const KIND_LABEL = { NATURAL: "Natural person", LEGAL: "Legal entity" };

const buildInitialValues = ({
  mode,
  clientOption,
  client,
  extractedPersonalData,
  extractedSecondaryPersonalData,
}) => {
  if (mode === "edit" && client) {
    const isLegal = client.buyer_type === "LEGAL";
    return {
      buyer_type: client.buyer_type,
      name: client.name || "",
      surname: client.surname || "",
      country: client.country || "",
      region: client.region || "",
      city: client.city || "",
      postal_code: client.postal_code || "",
      address: client.address || "",
      profession: client.profession || "",
      passport: client.passport || "",
      social_security_number: client.social_security_number || "",
      social_security_series: client.social_security_series || "",
      mobile_phone_number: client.mobile_phone_number || "",
      land_phone_number: client.land_phone_number || "",
      personal_email: client.personal_email || "",
      business_email: client.business_email || "",
      legal_company_name: client.legal_company_name || "",
      legal_fiscal_code: client.legal_fiscal_code || "",
      legal_unique_registration_code: client.legal_unique_registration_code || "",
      // Only default to "mirrored" when it already matches — an existing
      // client whose codes genuinely differ shouldn't get silently overwritten.
      legal_urc_same_as_fiscal: isLegal
        ? !!client.legal_unique_registration_code &&
          client.legal_unique_registration_code === client.legal_fiscal_code
        : true,
      legal_trade_register_number: client.legal_trade_register_number || "",
      legal_vat_payer: client.legal_vat_payer ?? true,
      iban_ron: client.iban_ron || "",
      iban_euro: client.iban_euro || "",
      // The Linked step doesn't exist in edit mode (see ClientModal's FLOW) —
      // linking/unlinking an existing client stays on the details page itself.
      add_second_client: false,
      additional_contact1: null,
      additional_contact2_id: null,
      additional_contact2_name: "",
      additional_contact2_surname: "",
      additional_contact2_ssn: "",
      additional_contact2_ssn_series: "",
      additional_contact2_mobile: "",
      additional_contact2_email: "",
      active: client.active ?? true,
    };
  }

  return {
    buyer_type: clientOption,
    name: extractedPersonalData?.name || "",
    surname: extractedPersonalData?.surname || null,
    country: extractedPersonalData?.country || null,
    region: extractedPersonalData?.region || null,
    city: extractedPersonalData?.city || null,
    postal_code: null,
    address: extractedPersonalData?.address || null,
    profession: null,
    passport: null,
    social_security_number: extractedPersonalData?.social_security_number || null,
    social_security_series: extractedPersonalData?.social_security_series || null,
    mobile_phone_number: extractedPersonalData?.mobile_phone_number || "",
    land_phone_number: null,
    personal_email: extractedPersonalData?.personal_email || null,
    business_email: extractedPersonalData?.business_email || null,
    legal_company_name: extractedPersonalData?.legal_company_name || null,
    legal_fiscal_code: extractedPersonalData?.legal_fiscal_code || null,
    legal_unique_registration_code: null,
    legal_urc_same_as_fiscal: true,
    legal_trade_register_number: extractedPersonalData?.legal_trade_register_number || null,
    legal_vat_payer: true,
    iban_ron: "",
    iban_euro: "",
    add_second_client: !!extractedSecondaryPersonalData,
    additional_contact1: null,
    additional_contact2_id: null,
    additional_contact2_name: extractedSecondaryPersonalData?.name || "",
    additional_contact2_surname: extractedSecondaryPersonalData?.surname || "",
    additional_contact2_ssn: extractedSecondaryPersonalData?.social_security_number || "",
    additional_contact2_ssn_series: extractedSecondaryPersonalData?.social_security_series || "",
    additional_contact2_mobile: extractedSecondaryPersonalData?.mobile_phone_number || "",
    additional_contact2_email: extractedSecondaryPersonalData?.personal_email || "",
    active: true,
  };
};

// Shared shape between create's plain branch and edit's submit — everything
// ClientSerializer accepts except the linking fields (additional_contact1/
// additional_contact2_id), which each caller sets on top since they mean
// different things in each flow.
const buildClientFieldsPayload = (values, effectiveUrc) => ({
  buyer_type: values.buyer_type,
  name: values.name !== "" ? values.name : values.legal_company_name,
  surname: values.surname,
  country: values.country,
  region: values.region,
  city: values.city,
  postal_code: values.postal_code,
  address: values.address,
  profession: values.profession,
  passport: values.passport,
  social_security_number: values.social_security_number,
  social_security_series: values.social_security_series,
  mobile_phone_number: values.mobile_phone_number,
  land_phone_number: values.land_phone_number,
  personal_email: values.personal_email,
  business_email: values.business_email,
  legal_company_name: values.legal_company_name,
  legal_fiscal_code: values.legal_fiscal_code,
  legal_unique_registration_code: effectiveUrc,
  legal_trade_register_number: values.legal_trade_register_number,
  legal_vat_payer: values.legal_vat_payer,
  iban_ron: values.iban_ron,
  iban_euro: values.iban_euro,
});

// Create and edit share this wizard shell. In create mode, kind is inherited
// from whichever Clients List tab (Natural/Legal) the user opened it from —
// there's no Type step, unlike the Create Client mockup. In edit mode, kind
// is switchable right on the Identity step (see the pill buttons below).
const ClientModal = ({
  show,
  clientOption,
  onCloseClick,
  extractedPersonalData = null,
  extractedSecondaryPersonalData = null,
  onClientCreated = null,
  mode = "create",
  client = null,
  onClientUpdated = null,
}) => {
  const { clients } = useSelector((state) => state.Clients);
  const dispatch = useDispatch();

  const isEdit = mode === "edit";
  const originalBuyerType = client?.buyer_type;

  const [draftBuyerType, setDraftBuyerType] = useState(
    isEdit ? originalBuyerType : clientOption
  );
  const [step, setStep] = useState(0);
  const [visited, setVisited] = useState(0);

  // Re-sync draft kind/step whenever the modal (re)opens — it stays mounted
  // between opens, so without this a previous session's state would leak
  // into the next one (e.g. editing a legal client right after a natural one).
  useEffect(() => {
    if (!show) return;
    setDraftBuyerType(isEdit ? client?.buyer_type : clientOption);
    setStep(0);
    setVisited(0);
  }, [show, isEdit, client, clientOption]);

  const isNaturalClient = draftBuyerType === "NATURAL";

  const FLOW = [
    "Identity",
    "Contact",
    "Address",
    "Banking",
    ...(!isEdit && isNaturalClient ? ["Linked"] : []),
    "Review",
  ];

  const currentStep = FLOW[Math.min(step, FLOW.length - 1)];
  const onReview = currentStep === "Review";
  const onLinked = currentStep === "Linked";
  const onIdentity = currentStep === "Identity";

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: buildInitialValues({
      mode,
      clientOption,
      client,
      extractedPersonalData,
      extractedSecondaryPersonalData,
    }),
    validationSchema: getStepValidationSchema(currentStep, isNaturalClient, clients),

    onSubmit: (values) => {
      // "Same as fiscal code" mirrors legal_fiscal_code without writing it
      // back into legal_unique_registration_code itself (see fieldConfig.js's
      // getEffectiveValue) — resolve the value actually being submitted here.
      const effectiveUrc = values.legal_urc_same_as_fiscal
        ? values.legal_fiscal_code || ""
        : values.legal_unique_registration_code;

      if (isEdit) {
        const payload = { id: client.id, ...buildClientFieldsPayload(values, effectiveUrc) };
        dispatch(onUpdateClient(payload))
          .then(() => {
            onClientUpdated?.();
            handleCloseModal();
          })
          .catch((error) => {
            console.error("Error updating client:", error);
          });
        return;
      }

      if (values.buyer_type === "NATURAL" && values.add_second_client) {
        const secondClient = {
          buyer_type: "NATURAL",
          name: values.additional_contact2_name,
          surname: values.additional_contact2_surname,
          social_security_number: values.additional_contact2_ssn,
          social_security_series: values.additional_contact2_ssn_series,
          mobile_phone_number: values.additional_contact2_mobile,
          personal_email: values.additional_contact2_email,
          country: values.country,
          region: values.region,
          city: values.city,
          postal_code: values.postal_code,
          address: values.address,
          active: true,
        };

        dispatch(onAddNewClient(secondClient))
          .then((secondClientResult) => {
            const secondClientId = secondClientResult.payload.id;

            const primaryClient = {
              ...buildClientFieldsPayload(values, effectiveUrc),
              additional_contact1: secondClientId,
              additional_contact2_id: null,
              active: values.active,
            };

            dispatch(onAddNewClient(primaryClient))
              .then((primaryClientResult) => {
                onClientCreated?.(primaryClientResult.payload.id);
                handleCloseModal();
              })
              .catch((error) => {
                console.error("Error creating primary client:", error);
              });
          })
          .catch((error) => {
            console.error("Error creating second client:", error);
          });
      } else {
        const newClient = {
          ...buildClientFieldsPayload(values, effectiveUrc),
          additional_contact1: null,
          additional_contact2_id: null,
          active: values.active,
        };

        dispatch(onAddNewClient(newClient))
          .then((newClientResult) => {
            onClientCreated?.(newClientResult.payload.id);
            handleCloseModal();
          })
          .catch((error) => {
            console.error("Error in client creation:", error);
          });
      }
    },
  });

  const handleCloseModal = () => {
    onCloseClick();
    setStep(0);
    setVisited(0);
    validation.resetForm();
  };

  const handleKindSwitch = (newKind) => {
    if (newKind === draftBuyerType) return;
    setDraftBuyerType(newKind);
    validation.setFieldValue("buyer_type", newKind);
    const clearKeys = newKind === "NATURAL" ? LEGAL_IDENTITY_KEYS : NATURAL_IDENTITY_KEYS;
    clearKeys.forEach((key) => validation.setFieldValue(key, ""));
    validation.setFieldValue("legal_vat_payer", true);
    validation.setFieldValue("legal_urc_same_as_fiscal", true);
    setStep(0);
    setVisited(0);
  };

  const goToStep = (name) => {
    const idx = FLOW.indexOf(name);
    if (idx !== -1 && idx <= visited) setStep(idx);
  };

  async function handleNext() {
    if (onReview) {
      validation.handleSubmit();
      return;
    }
    const errors = await validation.validateForm();
    if (Object.keys(errors).length === 0) {
      const nextStep = Math.min(step + 1, FLOW.length - 1);
      setStep(nextStep);
      setVisited((v) => Math.max(v, nextStep));
    } else {
      validation.setTouched(
        Object.keys(validation.values).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {}),
        true
      );
    }
  }

  const handleBack = () => setStep((s) => Math.max(0, s - 1));

  const stepIssues = FLOW.reduce((acc, name) => {
    acc[name] = countStepIssues(name, isNaturalClient, clients, validation.values);
    return acc;
  }, {});
  const allIssues = FLOW.filter((n) => n !== "Review").reduce((a, n) => a + stepIssues[n], 0);
  const currentIssues = onReview ? allIssues : stepIssues[currentStep];
  const ok = currentIssues === 0;

  const clientName = isNaturalClient
    ? [validation.values.name, validation.values.surname].filter(Boolean).join(" ")
    : validation.values.legal_company_name;
  const subtitle = clientName
    ? `${clientName} · ${KIND_LABEL[draftBuyerType] || ""}`
    : `Step ${step + 1} of ${FLOW.length} · ${currentStep}`;

  const stepFieldsFor = (name) => {
    switch (name) {
      case "Identity":
        return getIdentityFields(isNaturalClient);
      case "Contact":
        return getContactFields(isNaturalClient);
      case "Address":
        return getAddressFields(isNaturalClient);
      case "Banking":
        return getBankingFields();
      default:
        return [];
    }
  };

  return (
    <Modal
      isOpen={show}
      toggle={handleCloseModal}
      centered
      style={{ maxWidth: 660, width: "100%" }}
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          if (onReview) validation.handleSubmit();
          return false;
        }}
      >
        <div className="d-flex align-items-start gap-3 px-4 pt-3 pb-3 border-bottom">
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 650 }}>
              {isEdit ? "Edit client" : "New client"}
            </div>
            <div className="text-muted" style={{ fontSize: 13, marginTop: 3 }}>
              {subtitle}
            </div>
          </div>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={handleCloseModal}
          ></button>
        </div>

        <div
          className="d-flex flex-wrap px-4 py-2 border-bottom"
          style={{ background: "var(--vz-light)" }}
        >
          {FLOW.map((name, i) => {
            const done = i < step;
            const active = i === step;
            const issues = stepIssues[name];
            const clickable = i <= visited;
            const markBg = active
              ? "var(--vz-primary)"
              : done
              ? issues
                ? "#fdf7e7"
                : "#e6f4ec"
              : "#fff";
            const markFg = active
              ? "#fff"
              : done
              ? issues
                ? "var(--vz-warning)"
                : "var(--vz-success)"
              : "var(--vz-secondary)";
            const markBorder = active
              ? "var(--vz-primary)"
              : done
              ? issues
                ? "var(--vz-warning)"
                : "var(--vz-success)"
              : "var(--vz-border-color)";

            return (
              <div
                key={name}
                onClick={() => clickable && goToStep(name)}
                className="d-flex align-items-center gap-2 me-3 my-1"
                style={{ cursor: clickable ? "pointer" : "default" }}
              >
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: 22,
                    height: 22,
                    flex: "none",
                    borderRadius: "50%",
                    fontSize: 11,
                    fontWeight: 700,
                    background: markBg,
                    color: markFg,
                    border: `1.5px solid ${markBorder}`,
                  }}
                >
                  {done ? (issues ? "!" : "✓") : i + 1}
                </div>
                <span style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap" }}>
                  {name}
                </span>
              </div>
            );
          })}
        </div>

        <ModalBody style={{ maxHeight: "55vh", overflowY: "auto" }}>
          {onIdentity && (
            <div className="mb-3">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span className="text-muted" style={{ fontSize: 12.5 }}>
                  Client type
                </span>
                <div className="d-flex gap-2 flex-wrap">
                  {["NATURAL", "LEGAL"].map((kind) => (
                    <button
                      key={kind}
                      type="button"
                      onClick={() => handleKindSwitch(kind)}
                      className={`btn btn-sm ${
                        draftBuyerType === kind ? "btn-primary" : "btn-outline-secondary"
                      }`}
                    >
                      {KIND_LABEL[kind]}
                    </button>
                  ))}
                </div>
              </div>
              {isEdit && draftBuyerType !== originalBuyerType && (
                <div className="alert alert-warning py-2 px-3 mt-2 mb-0" style={{ fontSize: 12.5 }}>
                  {draftBuyerType === "LEGAL"
                    ? "Switching to a legal entity replaces the personal identity fields with company registration fields. Contact, address and banking data are kept."
                    : "Switching to a natural person replaces the company registration fields with personal identity fields. Contact, address and banking data are kept."}
                </div>
              )}
            </div>
          )}
          {onReview ? (
            <ReviewStep validation={validation} isNaturalClient={isNaturalClient} onEditStep={goToStep} />
          ) : onLinked ? (
            <LinkedStep validation={validation} />
          ) : (
            <FieldsStep fields={stepFieldsFor(currentStep)} validation={validation} />
          )}
        </ModalBody>

        <div
          className="d-flex flex-wrap align-items-center gap-2 px-4 py-3 border-top"
          style={{ background: "var(--vz-light)" }}
        >
          <Button color="light" onClick={handleBack} disabled={step === 0}>
            &larr; Back
          </Button>
          <span
            className="flex-grow-1"
            style={{ fontSize: 12.5, color: ok ? "var(--vz-success)" : "var(--vz-danger)" }}
          >
            {ok
              ? onReview
                ? "Everything checks out"
                : "This step is complete"
              : `${currentIssues} field${currentIssues === 1 ? "" : "s"} need attention`}
          </span>
          <Button color="primary" onClick={handleNext} disabled={!ok}>
            {onReview ? (isEdit ? "Save changes" : "Create client") : "Continue →"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

ClientModal.propTypes = {
  onCloseClick: PropTypes.func,
  show: PropTypes.any,
  clientOption: PropTypes.string,
  extractedPersonalData: PropTypes.object,
  extractedSecondaryPersonalData: PropTypes.object,
  onClientCreated: PropTypes.func,
  mode: PropTypes.oneOf(["create", "edit"]),
  client: PropTypes.object,
  onClientUpdated: PropTypes.func,
};

export default ClientModal;
